import data from "./products.json";

export interface HeroIngredient {
  name: string;
  benefits: string;
}

export interface Product {
  id: number;
  sku: string;
  name: string;
  description: string;
  category: string;
  volume: string;
  ph: string;
  shelfLife: string;
  skinTypes: string[];
  timeOfUse: string[];
  howToUse: string[];
  keyBenefits: string[];
  inciList: string;
  heroIngredients: HeroIngredient[];
  formulation_id: string;
  barcode: string;
  // Marketing fields
  step?: number;
  stepName?: string;
  shortName?: string;
  targets?: string[];
  cosmeticAnalysisRating?: string;
  freeFrom?: string[];
  notes?: string;
  // Commerce fields
  image?: string;
  images?: string[];
  shots?: Record<string, string[]>;
  /** Single source of truth. TRY/USD are derived at display time via fx.ts. */
  priceEUR?: number;
}

export interface ProductCategory {
  slug: string;
  image: string;
}

export const productCategories: ProductCategory[] = data.productCategories;
export const products: Product[] = data.products as Product[];

export function getProductsByCategory(category: string): Product[] {
  return products.filter((p) => p.category === category);
}

export function getProductBySku(sku: string): Product | undefined {
  return products.find((p) => p.sku === sku);
}

export function getAllProducts(): Product[] {
  return products;
}

export function getRelatedProducts(currentProduct: Product, limit = 3): Product[] {
  // Same category, exclude current product
  const sameCategory = products.filter(
    (p) => p.category === currentProduct.category && p.sku !== currentProduct.sku
  );

  if (sameCategory.length >= limit) {
    return sameCategory.slice(0, limit);
  }

  // Fill remaining slots with products sharing overlapping skin types
  const remaining = products.filter(
    (p) =>
      p.sku !== currentProduct.sku &&
      !sameCategory.some((s) => s.sku === p.sku) &&
      p.skinTypes.some((t) => currentProduct.skinTypes.includes(t))
  );

  return [...sameCategory, ...remaining].slice(0, limit);
}

export interface StepCompanion {
  step: number;
  label: string;
  product: Product | null;
  isCurrent: boolean;
}

export function getStepCompanions(currentProduct: Product): StepCompanion[] {
  const stepLabels: Record<number, string> = {
    1: "Cleanse",
    2: "Tone & Balance",
    3: "Focus & Treat",
    4: "Boost & Hydrate",
    5: "Protect",
  };

  const companions: StepCompanion[] = [];

  for (let step = 1; step <= 5; step++) {
    if (step === currentProduct.step) {
      companions.push({
        step,
        label: stepLabels[step],
        product: currentProduct,
        isCurrent: true,
      });
      continue;
    }

    // Priority 1: same category
    let match = products.find(
      (p) => p.step === step && p.category === currentProduct.category
    );

    // Priority 2: overlapping skin types
    if (!match) {
      match = products.find(
        (p) =>
          p.step === step &&
          p.skinTypes.some((t) => currentProduct.skinTypes.includes(t))
      );
    }

    // Priority 3: any product for that step
    if (!match) {
      match = products.find((p) => p.step === step);
    }

    companions.push({
      step,
      label: stepLabels[step],
      product: match || null,
      isCurrent: false,
    });
  }

  return companions;
}
